import React, { useState, useEffect, MouseEvent } from 'react'
import styled from 'styled-typed'
import { Row, Col } from 'uiComponents/flex'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { ArticleCrudData } from 'admin/articleService'
import { SingleSelect, SingleSelectOption, Toggle } from 'uiComponents/input'
import { LinkText } from 'uiComponents/typography'
import { InfoCard } from 'uiComponents/cards'
import { Template } from 'ticketTemplates/ticketTemplatesService'
import { FormItemName } from 'uiComponents/form/formElements'
import { LEFT_SIDE_SPAN, RIGHT_SIDE_SPAN } from './common'
import { useTranslation, Trans } from 'translations'

const TogglerCol = styled(Col)`
    display: flex;
    justify-content: space-between;
    align-items: center;
`
const Toggler = styled(Toggle)`
    font-size: 0.875em;
`

interface ProductFormProps {
    accountSlug: string
    ticketTemplatesList: Template[]
    productData: ArticleCrudData
    onTicketTemplateSelect: (id: string) => void
    onPrintTicketTemplateSelect: (id: string | null) => void
    onExternalNavigate: (e: MouseEvent, destination: string) => void
}

export function ETicketsSection(props: ProductFormProps) {
    const { t } = useTranslation()
    const [ticketTemplatesList, setTicketTemplatesList] = useState<SingleSelectOption[]>([])
    const [cardTemplatesList, setCardTemplatesList] = useState<SingleSelectOption[]>([])
    const [showCardTemplates, setShowCardTemplates] = useState<boolean>(false)
    const ticketTemplatesLink = `/account/${props.accountSlug}/engage/ticket_templates`

    const mapTemplatesLists = () => {
        const ticketOptions = props.ticketTemplatesList
            .filter((template) => ['TICKET', 'VOUCHER'].includes(template.templateType))
            .map((template) => ({
                name: template.name,
                value: template.id,
            }))
        ticketOptions.unshift({ value: '', name: t('products.crud.eTickets.none') })
        setTicketTemplatesList(ticketOptions)
        const cardOptions = props.ticketTemplatesList
            .filter((template) => template.templateType === 'CARD')
            .map((template) => ({
                name: template.name,
                value: template.id,
            }))
        cardOptions.unshift({ value: '', name: t('products.crud.eTickets.none') })
        setCardTemplatesList(cardOptions)
    }

    useEffect(() => {
        mapTemplatesLists()
    }, [props.ticketTemplatesList])

    useEffect(() => {
        if (
            !!props.productData.ticketPrintTemplateConfig &&
            !!props.productData.ticketPrintTemplateConfig !== showCardTemplates
        ) {
            setShowCardTemplates(!!props.productData.ticketPrintTemplateConfig)
        }
    }, [props.productData])

    const togglePrintTemplateSelect = () => {
        setShowCardTemplates(!showCardTemplates)
        if (showCardTemplates) {
            props.onPrintTicketTemplateSelect(null)
        }
    }

    return (
        <>
            <Row>
                <Col span={LEFT_SIDE_SPAN}>
                    <DisabledItemHoverInfotip
                        active={ticketTemplatesList.length === 0}
                        infotipText={t('products.crud.eTickets.productSpecificTemplateInfotip')}
                        width="30em"
                    >
                        <SingleSelect
                            options={ticketTemplatesList}
                            selected={props.productData.ticketTemplateConfig || null}
                            noSelectOption={t('products.crud.eTickets.selectTemplate')}
                            height="2.8em"
                            maxHeight="50em"
                            onSelect={props.onTicketTemplateSelect}
                            width="100%"
                        />
                    </DisabledItemHoverInfotip>
                    <LinkText>
                        <Trans
                            i18nKey="products.crud.eTickets.ticketTemplatesLink"
                            components={{
                                anchor: (
                                    <a
                                        href={ticketTemplatesLink}
                                        onClick={(e) => props.onExternalNavigate(e, ticketTemplatesLink)}
                                    />
                                ),
                            }}
                        />
                    </LinkText>
                </Col>
                <Col span={RIGHT_SIDE_SPAN}>
                    {!props.productData.ticketTemplateConfig && ticketTemplatesList.length > 0 && (
                        <InfoCard
                            active
                            type="info"
                            cardHeader=""
                            cardText={t('products.crud.eTickets.productSpecificTemplateCard')}
                        />
                    )}
                </Col>
            </Row>
            <Row style={{ marginTop: '1.5em' }}>
                <TogglerCol span={LEFT_SIDE_SPAN}>
                    <FormItemName>{t('products.crud.eTickets.seasonPassPrintVersion')}</FormItemName>
                    <Toggler
                        id="print-template"
                        isOn={showCardTemplates || !!props.productData.ticketPrintTemplateConfig}
                        onClick={togglePrintTemplateSelect}
                    />
                </TogglerCol>
                <Col span={RIGHT_SIDE_SPAN} />
            </Row>
            {showCardTemplates && (
                <Row style={{ marginTop: '0.5em' }}>
                    <Col span={LEFT_SIDE_SPAN}>
                        <DisabledItemHoverInfotip
                            active={cardTemplatesList.length === 0}
                            infotipText={t('products.crud.eTickets.noSeasonPassTemplates')}
                            width="30em"
                        >
                            <SingleSelect
                                options={cardTemplatesList}
                                selected={props.productData.ticketPrintTemplateConfig || null}
                                noSelectOption={t('products.crud.eTickets.selectTemplate')}
                                height="2.8em"
                                maxHeight="50em"
                                onSelect={props.onPrintTicketTemplateSelect}
                                width="100%"
                            />
                        </DisabledItemHoverInfotip>
                    </Col>
                    <Col span={RIGHT_SIDE_SPAN} />
                </Row>
            )}
        </>
    )
}
